import { debugLog } from "./utils/debugLog.js";
import React, { useState, useEffect } from "react";
import Notification from "./Notification";
import useBase from "./useBase";
import { useSignUp, useSignIn, useClerk } from "@clerk/clerk-react";
import {
	extractDomain,
	setContents,
	setShowNotif,
	includesSpecialCharacters,
	containsNumber,
	isManagedClient,
} from "./helpers";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { StarIcon } from '@heroicons/react/20/solid'
import { Tooltip } from "@material-tailwind/react";
import { useNavigate } from "react-router-dom";
// Removed flowbite carousel - using custom implementation
import brandonMinaImage from "./assets/brandon-mina-linkedin-profile.jpeg";
import Airtable from 'airtable';
import { ensureUserInAirtable } from './services/ensureUserInAirtable';

const apiKey = process.env.REACT_APP_AIR_KEY;
const baseId = process.env.REACT_APP_AIR_BASEID;
const usersTableId = process.env.REACT_APP_AIR_USERS;
const airtableBase = new Airtable({ apiKey }).base(baseId);

// Personal email domains
const PERSONAL_EMAIL_DOMAINS = [
	'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.ca', 'yahoo.co.uk',
	'outlook.com', 'hotmail.com', 'hotmail.ca', 'live.com', 'msn.com',
	'icloud.com', 'me.com', 'aol.com', 'protonmail.com', 'proton.me', 'mail.com', 'zoho.com'
];

const isPersonalEmail = (email) => {
	if (!email) return true;
	const domain = extractDomain(email)?.toLowerCase();
	return PERSONAL_EMAIL_DOMAINS.includes(domain);
};
const specialCharacters = `\!"#$%&'()*+,-./:;<=>?@[\]^_\{|}~`;



function SignUpPage({ isFreeSignup = false }) {
	const { isLoaded: isSignUpLoaded, signUp, setActive } = useSignUp();
	const { isLoaded: isSignInLoaded, signIn } = useSignIn();
	const clerk = useClerk();
	// Use same table as ensureUserInAirtable (REACT_APP_AIR_USERS) for consistent duplicate check
	const { loading: usersLoading, data: users, error: usersError } = useBase(usersTableId || "users");
	const [isFocused, setIsFocused] = useState(false);
	const navigate = useNavigate();

	const [userEmail, setUserEmail] = useState("");
	const [userPassword, setUserPassword] = useState("");
	const [confirmUserPassword, setConfirmUserPassword] = useState("");
	const [pendingVerification, setPendingVerification] = useState(false);
	const [showSignUpNotification, setShowSignUpNotification] = useState({
		showNotif: false,
		contents: { type: "", heading: "", message: "" },
	});
	const [passwordRequirements, setPasswordRequirements] = useState({
		minimumLength: {
			message: "Minimum password length of 8",
			fulfilled: false,
		},
		specialChar: {
			message: `At least 1 special character from: ${specialCharacters}`,
			fulfilled: false,
		},
		number: {
			message: "At least 1 number",
			fulfilled: false,
		},
		// confirmPasswordMatches: {
		// 	message: "Passwords match",
		// 	fulfilled: false,
		// },
	});


	const errorInSignUpFields = () => {
		// Basic email format check (relaxed - just check for @ and .)
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(userEmail) && !userEmail.includes("+clerk_test")) {
			let contents = { type: "error", heading: "Invalid Email Address", message: "Please enter a valid email address." };
			setContents(contents, setShowSignUpNotification);
			setShowNotif(true, setShowSignUpNotification);
			return true;
		}

		// For personal emails, strongly recommend OAuth but don't block
		// Note: Clerk may still block if configured to do so in the dashboard

		// Skip Airtable duplicate check for Clerk test emails (+clerk_test) - Clerk handles persistence
		const usersWithSameEmail = userEmail.includes("+clerk_test")
			? []
			: (users?.filter((user) => user?.fields?.Email?.toLowerCase() === userEmail.toLowerCase()) || []);
		if (usersWithSameEmail.length !== 0) {
			let contents = {
                type: "error",
                heading: "Email is Already Signed Up",
                message: `Please sign-in with ${userEmail} or click forgot password.`,
            };
            setContents(contents, setShowSignUpNotification);
            setShowNotif(true, setShowSignUpNotification);
			
			navigate(`/sign-in${window.location.search}`);
			return true;
		}

		if (
			Object.keys(passwordRequirements)
				.map((key) => passwordRequirements[key].fulfilled)
				.includes(false)
		) {
			let contents = {
				type: "error",
				heading: "Invalid Password",
				message: `Please create a valid password.`,
			};
			setContents(contents, setShowSignUpNotification);
			setShowNotif(true, setShowSignUpNotification);

			return true;
		}

		return false;
	};

	const handleSignUp = async (event) => {
		event.preventDefault();
		if (!isSignUpLoaded) return;
		if (usersLoading) return; // Wait for Airtable users to load so we can check for existing emails

		if (errorInSignUpFields()) return;

		try {
			await signUp.create({
				emailAddress: userEmail,
				password: userPassword,
			});

			await signUp.prepareEmailAddressVerification({ strategy: "email_code" });

			setPendingVerification(true);
		} catch (e) {
			console.error("Sign-up error:", e);
			debugLog("Full error details:", JSON.stringify(e, null, 2));
			
			const emailExistsError = e.errors?.find((err) => err.code === "form_identifier_exists");

			if (emailExistsError) {
				// User exists in Clerk (e.g. +clerk_test from prior test) - auto sign-in and ensure Airtable record
				if (!isSignInLoaded) {
					navigate(`/sign-in?error=already_exists${window.location.search ? `&${window.location.search.slice(1)}` : ''}`);
					return;
				}
				try {
					const signInResult = await signIn.create({
						identifier: userEmail,
						password: userPassword,
					});
					if (signInResult.status === "complete") {
						const userEmailLower = userEmail.toLowerCase();
						// Ensure user exists in Airtable before setActive so we know where to navigate
						let result = { companyId: null, companyDomain: null, companyName: null };
						try {
							result = await ensureUserInAirtable({
								email: userEmailLower,
								fullName: '',
								airtableBase,
							});
						} catch (airtableErr) {
							console.warn('ensureUserInAirtable failed after sign-in, WorkspaceSelectionPage will retry:', airtableErr);
						}
						await setActive({ session: signInResult.createdSessionId });
						const params = new URLSearchParams(window.location.search);
						const redirectUrl = params.get('redirect');
						const inviteToken = params.get('invite');
						let targetPath = inviteToken ? `/workspace-selection?invite=${inviteToken}` : '/workspace-selection';
						if (result?.companyId) {
							try {
								const company = await airtableBase('companies').find(result.companyId);
								const isManaged = isManagedClient(company);
								if (isManaged) {
									targetPath = redirectUrl || `/grants-section?website=${encodeURIComponent(result.companyDomain || '')}&company=${encodeURIComponent(result.companyName || '')}`;
								} else {
									targetPath = `/onboarding-v2?company=${encodeURIComponent(result.companyId)}`;
								}
							} catch {
								targetPath = `/onboarding-v2?company=${encodeURIComponent(result.companyId)}`;
							}
						}
						const target = targetPath.startsWith('http') ? targetPath : `${window.location.origin}${targetPath.startsWith('/') ? targetPath : `/${targetPath}`}`;
						await new Promise((r) => setTimeout(r, 800));
						window.location.href = target;
						return;
					}
				} catch (signInErr) {
					console.warn('Auto sign-in failed, redirecting to sign-in:', signInErr);
				}
				const search = window.location.search ? `&${window.location.search.slice(1)}` : "";
				navigate(`/sign-in?error=already_exists${search}`);
				return;
			}

			// "You're already signed in" or "Session already exists" - user has an active session
			const alreadySignedIn = e.errors?.some((err) =>
				err.message?.toLowerCase().includes("already signed in") ||
				err.longMessage?.toLowerCase().includes("already signed in") ||
				err.message?.toLowerCase().includes("session already exists") ||
				err.longMessage?.toLowerCase().includes("session already exists")
			) || e.message?.toLowerCase().includes("already signed in") || e.message?.toLowerCase().includes("session already exists");
			if (alreadySignedIn) {
				navigate(`/check_oauth_google${window.location.search || ""}`);
				return;
			}

			// "No sign up attempt was found" - session was lost (page refresh, expired, etc.)
			// User may have just created account - direct to sign-in with session_expired (different message)
			const noSignUpAttempt = e.errors?.some((err) =>
				err.message?.toLowerCase().includes("no sign up attempt") ||
				err.longMessage?.toLowerCase().includes("no sign up attempt")
			) || e.message?.toLowerCase().includes("no sign up attempt");
			if (noSignUpAttempt) {
				const search = window.location.search ? `&${window.location.search.slice(1)}` : "";
				navigate(`/sign-in?error=session_expired${search}`);
				return;
			}

			// Check for blocked email domain error from Clerk
			const isBlockedEmailError = e.errors?.some((err) => 
				err.code === "form_param_format_invalid" || 
				err.code === "form_identifier_not_allowed" ||
				err.message?.toLowerCase().includes("blocked") ||
				err.message?.toLowerCase().includes("not allowed") ||
				err.message?.toLowerCase().includes("email address is not allowed")
			);
			
			let errorMessage = `There was an error in signing-up, please try again later.`;
			let errorHeading = "Sign-Up Error";
			
			// Provide more specific error messages
			if (e.errors && e.errors.length > 0) {
				const firstError = e.errors[0];
				if (firstError.longMessage) {
					errorMessage = firstError.longMessage;
				} else if (firstError.message) {
					errorMessage = firstError.message;
				}
			}
			
			// Note: If personal emails are blocked, ensure Clerk Dashboard 
			// (User & Authentication → Restrictions) allows personal email domains
			
			let contents = {
				type: "error",
				heading: errorHeading,
				message: errorMessage,
			};
			setContents(contents, setShowSignUpNotification);
			setShowNotif(true, setShowSignUpNotification);
		}
	};

	const onPressVerify = async (code) => {
		if (!isSignUpLoaded) return;

		try {
			const completeSignUp = await signUp.attemptEmailAddressVerification({
				code: code,
			});

			if (completeSignUp.status !== "complete") {
				debugLog(JSON.stringify(completeSignUp, null, 2));
			} else {
				// Ensure user exists in Airtable (creates if new, returns existing if approved before signup)
				const userEmailLower = userEmail.toLowerCase();
				const extractedDomain = extractDomain(userEmailLower);
				const isPersonal = isPersonalEmail(userEmailLower);

				if (process.env.REACT_APP_DEBUG_AUTH === 'true') {
					debugLog('[SignUpPage] onPressVerify: calling ensureUserInAirtable', { email: userEmailLower.slice(0, 3) + '***' });
				}
				// Create user in Airtable immediately on valid verification - required before proceeding
				const result = await ensureUserInAirtable({
					email: userEmailLower,
					fullName: '', // Email/password form doesn't collect name
					airtableBase,
				});

				const userAirtableId = result.userAirtableId;
				if (process.env.REACT_APP_DEBUG_AUTH === 'true') {
					debugLog('[SignUpPage] ensureUserInAirtable result', { userAirtableId: !!userAirtableId });
				}

				// Block navigation if Airtable user creation failed (except rate limit - allow retry later)
				if (!userAirtableId && !result.skipped && result?.errorReason !== 'rate_limit') {
					console.error('[SignUpPage] Airtable user creation failed, cannot proceed');
					setContents(
						{ type: 'error', heading: 'Setup Incomplete', message: 'We couldn\'t create your account. Please try again or contact support.' },
						setShowSignUpNotification
					);
					setShowNotif(true, setShowSignUpNotification);
					return;
				}

				const companyId = result.companyId;
				let companyDomain = result.companyDomain;
				let companyName = result.companyName;

				// Fallback for companyDomain when ensureUserInAirtable returns null
				if (!companyDomain && extractedDomain) companyDomain = extractedDomain;

				await setActive({ session: completeSignUp.createdSessionId });

				// Sync Airtable ID to Clerk metadata for downstream routing/fallbacks
				if (userAirtableId && clerk.user) {
					try {
						await clerk.user.update({
							unsafeMetadata: { airtableId: userAirtableId },
							publicMetadata: {
								airtable_id: userAirtableId,
								company_id: companyId || undefined,
								company_domain: companyDomain || undefined,
								company_name: companyName || undefined,
								is_personal_email: isPersonal,
							},
						});
					} catch (metadataErr) {
						console.warn('[SignUpPage] Failed to update Clerk metadata:', metadataErr);
					}
				}
				
				// Send sign-up Slack notification (fire-and-forget; skip failure for rate_limit)
				const shouldAlertFailure = !userAirtableId && result?.errorReason !== 'rate_limit';
				if (userAirtableId || shouldAlertFailure) {
					fetch('/api/auth-notification', {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({
							email: userEmailLower,
							type: userAirtableId ? 'signup' : 'failure',
							companyName: companyName || undefined,
							domain: companyDomain || extractedDomain || undefined,
							reason: userAirtableId ? undefined : 'ensureUserInAirtable returned null',
						}),
					}).catch(err => console.error('Auth notification error:', err));
				}
				
				// Determine target - workspace-selection (default), onboarding-v2, or dashboard
				const params = new URLSearchParams(window.location.search);
				const redirectUrl = params.get('redirect');
				const inviteToken = params.get('invite');
				let targetPath = inviteToken ? `/workspace-selection?invite=${inviteToken}` : '/workspace-selection';
				
				if (companyId) {
					try {
						const company = await airtableBase('companies').find(companyId);
						const isManaged = isManagedClient(company);
						if (isManaged) {
							targetPath = redirectUrl || `/grants-section?website=${encodeURIComponent(companyDomain || extractedDomain || '')}&company=${encodeURIComponent(companyName || '')}`;
						} else {
							targetPath = redirectUrl
								? redirectUrl
								: `/onboarding-v2?company=${encodeURIComponent(companyId)}`;
						}
					} catch {
						targetPath = redirectUrl
							? redirectUrl
							: `/onboarding-v2?company=${encodeURIComponent(companyId)}`;
					}
				}
				
				// Full-page redirect: wait for Clerk to persist session cookie, then go directly.
				// navigate() races with Reroute (SignedIn /sign-up -> dashboard); full page avoids that.
				const target = targetPath.startsWith('http') ? targetPath : `${window.location.origin}${targetPath.startsWith('/') ? targetPath : `/${targetPath}`}`;
				await new Promise((r) => setTimeout(r, 800));
				window.location.href = target;
			}
		} catch (error) {
			console.error("Error in Verifying Email: ", error);

			// "No sign up attempt was found" - session was lost (page refresh, expired)
			// Account may have been created - direct to sign-in with session_expired
			const noSignUpAttempt = error?.errors?.some((err) =>
				err.message?.toLowerCase().includes("no sign up attempt") ||
				err.longMessage?.toLowerCase().includes("no sign up attempt")
			) || error?.message?.toLowerCase().includes("no sign up attempt");
			if (noSignUpAttempt) {
				const search = window.location.search ? `&${window.location.search.slice(1)}` : "";
				navigate(`/sign-in?error=session_expired${search}`);
				return;
			}

			// "You're already signed in" or "Session already exists" - completed in another tab
			const alreadySignedIn = error?.errors?.some((err) =>
				err.message?.toLowerCase().includes("already signed in") ||
				err.longMessage?.toLowerCase().includes("already signed in") ||
				err.message?.toLowerCase().includes("session already exists") ||
				err.longMessage?.toLowerCase().includes("session already exists")
			) || error?.message?.toLowerCase().includes("already signed in") || error?.message?.toLowerCase().includes("session already exists");
			if (alreadySignedIn) {
				navigate(`/check_oauth_google${window.location.search || ""}`);
				return;
			}

			let contents = {
				type: "error",
				heading: "Unable to Verify",
				message: `Please make sure you're entering the correct code.`,
			};
			setContents(contents, setShowSignUpNotification);
			setShowNotif(true, setShowSignUpNotification);
		}
	};

	const changePasswordFulfilledValue = (field, value) => {
		setPasswordRequirements((prev) => ({ ...prev, [field]: { ...prev[field], fulfilled: value } }));
	};

	const onPasswordChange = (e) => {
		const password = e.target.value;

		setUserPassword(password);
		
		onConfirmPasswordChange(e);

		// Minimum Length Checks
		if (passwordRequirements.minimumLength.fulfilled === false && password.length >= 8) {
			changePasswordFulfilledValue("minimumLength", true);
		} else if (passwordRequirements.minimumLength.fulfilled === true && password.length < 8) {
			changePasswordFulfilledValue("minimumLength", false);
		}

		// Special Char Checks
		if (passwordRequirements.specialChar.fulfilled === false && includesSpecialCharacters(specialCharacters, password)) {
			changePasswordFulfilledValue("specialChar", true);
		} else if (passwordRequirements.specialChar.fulfilled === true && !includesSpecialCharacters(specialCharacters, password)) {
			changePasswordFulfilledValue("specialChar", false);
		}

		// Number Checks
		if (passwordRequirements.number.fulfilled === false && containsNumber(password)) {
			changePasswordFulfilledValue("number", true);
		} else if (passwordRequirements.number.fulfilled === true && !containsNumber(password)) {
			changePasswordFulfilledValue("number", false);
		}

		// // 'Confirm Password' matches 'Password' Checks
		// if (passwordRequirements.confirmPasswordMatches.fulfilled === false && password === confirmUserPassword) {
		// 	changePasswordFulfilledValue("confirmPasswordMatches", true);
		// } else if (passwordRequirements.confirmPasswordMatches.fulfilled === true && password !== confirmUserPassword) {
		// 	changePasswordFulfilledValue("confirmPasswordMatches", false);
		// }

	};

	const onConfirmPasswordChange = (event) => {
		const confirmPassword = event.target.value;

		setConfirmUserPassword(confirmPassword);

		// if (passwordRequirements.confirmPasswordMatches.fulfilled === false && confirmPassword === userPassword) {
		// 	changePasswordFulfilledValue("confirmPasswordMatches", true);
		// } else if (passwordRequirements.confirmPasswordMatches.fulfilled === true && confirmPassword !== userPassword) {
		// 	changePasswordFulfilledValue("confirmPasswordMatches", false);
		// }
	};

	const signUpWith = (strategy) => {
		if (!isSignUpLoaded || !isSignInLoaded) return;

		// Store the free signup flag in session storage so it persists through OAuth
		if (isFreeSignup) {
			sessionStorage.setItem('freeSignup', 'true');
		}

		// Get redirect URL from query parameters
		const params = new URLSearchParams(window.location.search);
		const redirectUrl = params.get("redirect");
		const needsOnboarding = params.get("onboarding") === "true";
		if (redirectUrl) {
			sessionStorage.setItem('grantPageRedirectAfterOAuth', redirectUrl);
		}

		// All OAuth sign-ups go through check_oauth_google which will route to workspace-selection
		// The onboarding flag helps determine if they came from free signup
		let completeRedirectUrl;
		if (needsOnboarding || isFreeSignup) {
			completeRedirectUrl = "/check_oauth_google?onboarding=true";
		} else {
			completeRedirectUrl = "/check_oauth_google";
		}

		// Use signIn.authenticateWithRedirect for OAuth - it handles BOTH new and existing users.
		// signUp.authenticateWithRedirect fails with "No sign up attempt was found" when the user
		// already has an account, because Clerk doesn't create a sign-up attempt for existing users.
		// signIn with OAuth creates accounts for new users and signs in existing users seamlessly.
		signIn.authenticateWithRedirect({
			strategy,
			redirectUrl: "/sso-callback",
			redirectUrlComplete: completeRedirectUrl,
		});
	};

	useEffect(() => {
		const carouselElement = document.getElementById('carousel-testimonials');
		const items = [
			{
				position: 0,
				el: document.getElementById('carousel-indicator-0'),
			},
			{
				position: 1,
				el: document.getElementById('carousel-indicator-1'),
			},
			{
				position: 2,
				el: document.getElementById('carousel-indicator-2'),
			},
		];
		
		const options = {
			defaultPosition: 0,
			interval: false,
			indicators: {
				activeClasses: 'bg-gray-900 z-20',
				inactiveClasses: 'bg-gray-900 z-10',
				items: items,
			},
		};

		const instanceOptions = {
			id: 'carousel-testimonials',
			override: true
		};

		  // Carousel removed - using static display instead
	}, []);


	const testimonials = [
		{
			stars: 5,
			text: "“Seriously, do this today.\nThese grants & credits expire.\nOnce they're gone, they're gone.”",
			author: "Brandon Mina",
			title: "CEO, BrandPilot AI | (CSE: BPAI | OTCQB: BPAIF | FSE:8LHO)",
			image: brandonMinaImage,
		},
		{
			stars: 5,
			text: "“Upgraded found us $64,000/yr in savings on our first payroll cycle - we had no revenue and only 2 employees. Magic”",
			author: "Matt Parson",
			title: "Founder, Benji",
			image: "https://assets-global.website-files.com/655000b5d6f9d81d7029fd25/65f0a34f691a72257fea9cfb_matt-parson-portrait-p-500.jpeg",
		},
		{
			stars: 5,
			text: "“We hired the best CPA in Vancouver to look for credits. Upgraded still blew them out of the water.”",
			author: "Hannah Simmons",
			title: "Founder, ERA Brazil",
			image: "https://assets-global.website-files.com/634f60226f66af42a384d5b7/6369789c70e85f8693c7c324_hannah-simmons-profile.jpeg",
		},
		{
			stars: 5,
			text: "“We raised $100M from the best VCs. Upgraded still found us credits nobody had ever mentioned.”",
			author: "David McMurchy",
			title: "Sales Director, i-Sight",
			image: "https://assets-global.website-files.com/655000b5d6f9d81d7029fd25/65f1325f2d57b8c9b95895ee_david-headshot-1.jpeg",
		},
		// Add more as needed
		];

	return (
		<div>
			<div className="flex min-h-full flex-1 h-screen overflow-hidden">
				<div className="flex flex-1 flex-col justify-center px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
					<div className="mx-auto w-full max-w-sm lg:w-96">
						<div>
							<img
								className="h-11 w-auto max-w-[200px] object-contain"
								src="/upgraded-full-logo.png"
								alt="Upgraded"
							/>
							<h2 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-gray-900 text-left">Free Money, Found</h2>
							<p className="mt-2 text-sm leading-6 text-gray-500">
								Already a member?{" "}
								<a href="/sign-in" className="font-semibold text-sky-600 hover:text-sky-700 transition-colors duration-150">
									Sign In Instead
								</a>
							</p>
						</div>

						<div className="mt-10">
							<div className="grid grid-cols-1 gap-4">
								<button 
									className="flex w-full items-center justify-center gap-3 rounded-md bg-white px-3 py-3 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:ring-transparent transition-colors"
									onClick={() => {
										signUpWith("oauth_google")
									}}
								>
									<svg className="h-5 w-5" aria-hidden="true" viewBox="0 0 24 24">
										<path
											d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z"
											fill="#EA4335"
										/>
										<path
											d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z"
											fill="#4285F4"
										/>
										<path
											d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z"
											fill="#FBBC05"
										/>
										<path
											d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z"
											fill="#34A853"
										/>
									</svg>
									<span className="text-sm font-semibold leading-6">Continue with Google</span>
								</button>
								<p className="text-center text-xs text-gray-500">
									Recommended for Gmail, personal, or any email
								</p>
							</div>
						</div>
						<div className="relative mt-3 mb-4">
							<div className="absolute inset-0 flex items-center" aria-hidden="true">
								<div className="w-full border-t border-gray-200" />
							</div>
							<div className="relative flex justify-center text-sm font-medium leading-6">
								<span className="bg-white px-3 text-gray-500">OR</span>
							</div>
						</div>

						<div className="mt-3">
							<div>
								{!pendingVerification ? (
									<div>
										
										<form onSubmit={handleSignUp} className="space-y-6">
											<div>
												<label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">
													Email address
												</label>
												<div className="mt-2">
													<input
														id="email"
														name="email"
														type="email"
														autoComplete="email"
														placeholder="you@company.com"
														value={userEmail}
														onChange={(e) => setUserEmail(e.target.value)}
														required
														className="block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
													/>
												</div>
												<p className="mt-1 text-xs text-gray-500">
													Using Gmail? Use "Continue with Google" above for better results.
												</p>
											</div>

											<div>
												<label htmlFor="password" className="block text-sm font-medium leading-6 text-gray-900">
													Password
												</label>
												<div className="mt-2">
													<input
														id="password"
														name="password"
														type="password"
														required
														value={userPassword}
														onChange={onPasswordChange}
														onFocus={() => setIsFocused(true)}
														className="block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
													/>
												</div>
											</div>

											{/* <div>
												<label htmlFor="confirmPassword" className="block text-sm font-medium leading-6 text-gray-900">
													Confirm Password
												</label>
												<div className="mt-2">
													<input
														id="confirmPassword"
														name="confirmPassword"
														type="password"
														required
														autoComplete="new-password"
														value={confirmUserPassword}
														onChange={onConfirmPasswordChange}
														onFocus={() => setIsFocused(true)}
														className="block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
													/>
												</div>
											</div> */}

											{isFocused && <div className="grid grid-rows-3 mt-1 px-1">
												{Object.keys(passwordRequirements).map((key) => (
													<div key={key} className="inline-flex mt-1 space-x-1">
														<div className="flex text-center items-center mb-2 justify-center">
															{passwordRequirements[key].fulfilled ? (
																<CheckCircleIcon className="text-green-400 h-4 w-4" aria-hidden />
															) : (
																<XCircleIcon className="text-gray-400 h-4 w-4" aria-hidden />
															)}
														</div>
														<p className="mt-1 text-xs text-left">{passwordRequirements[key].message}</p>
													</div>
												))}
											</div>}

											<div>
												<button
													type="submit"
													disabled={usersLoading}
													className="flex w-full justify-center rounded-md bg-gray-900 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:opacity-75 transition-opacity duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#849E96] shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
												>
													{usersLoading ? "Checking..." : "Create Account"}
												</button>
											</div>
										</form>
										<br />
										<span className="text-gray-500 text-sm">By signing up, you acknowledge that you have read and understood, and agree to Upgraded's <a href="/terms-of-service" className="no-underline text-gray-900" target="_blank" rel="noreferrer">Terms</a> and <a href="/privacy-policy" className="no-underline text-gray-900" target="_blank" rel="noreferrer">Privacy Policy.</a></span>
									</div>
								) : (
									<form
										onSubmit={(e) => {
											e.preventDefault();
											const code = e.target["verificationCode"].value;
											onPressVerify(code);
										}}
									>
										<div>
											<label className="block text-sm font-medium leading-6 text-gray-900">Verify your Email</label>
											<p className="block text-xs font-small leading-6 text-gray-900">Enter the code sent to {userEmail}</p>
											<div className="mt-2">
												<input
													id="verificationCode"
													name="verificationCode"
													type="number"
													required
													autoComplete="one-time-code"
													className="block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
												/>
											</div>
										</div>
										<button
											className="mt-10 flex w-full justify-center rounded-md bg-[#849E96] px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:opacity-75 transition-opacity duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#849E96]"
											type="submit"
										>
											Submit
										</button>
									</form>
								)}
							</div>
						</div>
					</div>
				</div>
				<div className="relative hidden w-0 flex-1 lg:block my-auto py-auto h-screen overflow-hidden">
					{/* Beautiful palm frond background image */}
					<div 
						className="absolute inset-0 bg-cover bg-center bg-no-repeat"
						style={{
							backgroundImage: "url('https://images.pexels.com/photos/1581363/pexels-photo-1581363.jpeg')"
						}}
					/>
					{/* Overlay for better text readability */}
					<div className="absolute inset-0 bg-black/20" />
					
					<section className="relative px-6 py-8 sm:py-12 lg:px-8 h-full flex flex-col justify-center">
						<figure className="mx-auto max-w-2xl">
							<div id="carousel-testimonials" className="relative w-full mb-12 z-50" data-carousel="static">
								<div className="relative h-40 overflow-hidden rounded-lg sm:h-48 xl:h-56 2xl:h-64">
									{testimonials.map((testimonial, i) => (
										<div 
											key={i} 
											className="hidden duration-700 ease-in-out absolute inset-0 transition-all transform"
											data-carousel-item
											id={`carousel-indicator-${i}`}
										>
											<div className="absolute inset-0 bg-black/40 backdrop-blur-sm rounded-lg">
												<p className="sr-only">{testimonial.stars} out of 5 stars</p>
												<div className="flex gap-x-1 pt-3 pl-3 text-yellow-400">
													{Array.from({ length: testimonial.stars }).map((_, starIndex) => (
														<StarIcon key={starIndex} className="h-3 w-3 flex-none" aria-hidden="true" />
													))}
												</div>
												<blockquote className="mt-4 pl-3 leading-5 tracking-tight text-white text-base sm:text-lg sm:leading-6 whitespace-pre-line">
													<p>{testimonial.text}</p>
												</blockquote>
												<figcaption className="mt-4 flex pl-3 items-center gap-x-3">
													<img
														className="h-8 w-8 rounded-full bg-gray-900"
														src={testimonial.image}
														alt={testimonial.author}
													/>
													<div className="text-xs leading-4">
														<div className="font-semibold pl-1 text-white">{testimonial.author}</div>
														<div className="mt-0.5 pl-1 text-gray-200">{testimonial.title}</div>
													</div>
												</figcaption>
											</div>
										</div>
									))}
								</div>
							</div>
							{/* Centered text with adjusted sizing and positioning */}
							<div className="flex flex-col gap-6 justify-center items-center -mt-48">
								{[
									{
										value: "+$225,283/yr",
										label: "Secured on Average"
									},
									{
										value: "+1,778",
										label: "Grants & Government Programs"
									},
									{
										value: "+$2.9B/yr",
										label: "Funding Available"
									}
								].map((stat, index) => (
									<div 
										key={index}
										className="group text-center transition-all duration-300
												   border-b border-white/40 pb-3
												   hover:border-white/60"
									>
										<div 
											className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight"
											style={{ fontFamily: "'Playfair Display', serif" }}
										>
											{stat.value}
										</div>
										<div 
											className="mt-1 text-white/95 text-base sm:text-lg lg:text-xl font-medium"
											style={{ fontFamily: "'Crimson Text', serif" }}
										>
											{stat.label}
										</div>
									</div>
								))}
							</div>
						</figure>
					</section>
				</div>
			</div>
			<Notification
				showNotif={showSignUpNotification.showNotif}
				setShowNotif={(val) => setShowNotif(val, setShowSignUpNotification)}
				contents={showSignUpNotification.contents}
			/>
		</div>
	);
}

export default SignUpPage;
